import type { Observation } from "@medplum/fhirtypes";
import { getTRPCClient, OBSERVATION_ENTITY_ID_SYSTEM } from "@repo/api";
import {
  type RecordSymptomInput,
  recordSymptomInputSchema,
  type UpdateSymptomInput,
  updateSymptomInputSchema,
} from "@repo/api/schemas";
import { OBSERVATION_CATEGORY_SYSTEM as OBS_CATEGORY_SYSTEM } from "@repo/fhir-systems";
import { defineWrite } from "../actions/define-write";
import { getDataStoreClient } from "../core/provider";
import { DomainResultError } from "../core/write-errors";
import type { ConditionRow, ObservationRow } from "../fhir/types";
import { optimisticPatientReference } from "./_patient-reference";

const OBSERVATION_COLLECTION = "observation";
const CONDITION_COLLECTION = "condition";

const observations = () => getDataStoreClient().getCollection<ObservationRow, string>(OBSERVATION_COLLECTION);
const conditions = () => getDataStoreClient().getCollection<ConditionRow, string>(CONDITION_COLLECTION);

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function observationResourceId(domainId: string): string {
  if (!observations().has(domainId)) throw new Error(`Observation '${domainId}' is not loaded`);
  return domainId;
}

/** Rows are keyed by domain identity; this only asserts the row is loaded. */
function conditionResourceId(domainId: string): string {
  if (!conditions().has(domainId)) throw new Error(`Condition '${domainId}' is not loaded`);
  return domainId;
}

/**
 * Intent: "record a symptom" — creates a manifestation Observation (category
 * survey or exam) and appends it to each attached Condition's evidence list.

 */
export const recordSymptom = defineWrite<RecordSymptomInput>({
  optimistic: (params) => {
    const input = recordSymptomInputSchema.parse(params);
    const observationId = input.id;
    const categoryCode = input.source === "patient-reported" ? "survey" : "exam";

    observations().insert({
      resourceType: "Observation",
      // Widened: the row is `as ObservationRow`, and that cast needs FHIR's own
      // `system?: string` to stay comparable in both directions.
      identifier: [{ system: OBSERVATION_ENTITY_ID_SYSTEM as string, value: observationId }],
      domainId: observationId,
      status: "final",
      category: [
        {
          coding: [
            {
              // Like identifier.system above, the optimistic row cast needs FHIR's string type.
              system: OBS_CATEGORY_SYSTEM as string,
              code: categoryCode,
              display: categoryCode === "survey" ? "Survey" : "Exam",
            },
          ],
        },
      ],
      code: input.code,
      subject: optimisticPatientReference(input.patient.id),
      effectiveDateTime: input.effectiveDateTime ?? new Date().toISOString(),
      valueCodeableConcept: input.severity,
      note: input.note ? [{ text: input.note }] : undefined,
    } as ObservationRow);

    for (const { conditionId } of input.attachedTo ?? []) {
      conditions().update(conditionResourceId(conditionId), (draft) => {
        draft.evidence = [
          ...(draft.evidence ?? []),
          {
            code: [input.code],
            detail: [{ identifier: { system: OBSERVATION_ENTITY_ID_SYSTEM, value: observationId } }],
          },
        ];
      });
    }
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.symptom.record.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});

/**
 * Intent: "correct a symptom" — patches source (category), severity, and/or note
 * on the manifestation Observation. The code is immutable.
 */
export const updateSymptom = defineWrite<UpdateSymptomInput>({
  optimistic: (params) => {
    const input = updateSymptomInputSchema.parse(params);
    const collection = observations();
    const resourceId = observationResourceId(input.observationId);
    const changes: Partial<Observation> = {};
    if (input.source !== undefined || input.severity !== undefined || input.note !== undefined) {
      const status = collection.get(resourceId)?.status;
      if (status === "final" || status === "amended" || status === "corrected") changes.status = "amended";
    }
    if (input.source !== undefined) {
      const categoryCode = input.source === "patient-reported" ? "survey" : "exam";
      changes.category = [
        {
          coding: [
            { system: OBS_CATEGORY_SYSTEM, code: categoryCode, display: categoryCode === "survey" ? "Survey" : "Exam" },
          ],
        },
      ];
    }
    if (input.severity !== undefined) changes.valueCodeableConcept = input.severity;
    if (input.note !== undefined) changes.note = input.note ? [{ text: input.note }] : undefined;

    collection.update(resourceId, (draft) => {
      Object.assign(draft, changes);
    });
  },
  persist: async (params, options) => {
    const result = await getTRPCClient().charting.symptom.update.mutate(params, options);
    if (!result.success) throw new DomainResultError(result.error.message);
  },
});
